// src/encounter/risk/lib/risk-qr-bridge.ts
// THE RISK BRIDGE — template answers <-> EncounterMetaData Fall/Pain entries. Pure, no I/O.
//
// Ahmed's ruling (2026-09-24, PROD-2577; closes PROD-2354): the Risk step's CONTENT comes from
// the Template Studio template and renders through the DS QuestionnaireRenderer, but its ANSWERS
// persist to EncounterMetaData `FallAssessment` / `PainAssessment` — the store Flutter reads. So
// the renderer holds ordinary QuestionnaireResponse answers for the `fall-assessment` and
// `wong-baker-pain` groups, and this module translates them to and from the entry heads.
//
// THE WIRE SHAPE IS THE BESPOKE CARDS' SHAPE. Every mapping below reproduces how the retired
// `fall-assessment-card.tsx` / `pain-assessment-card.tsx` (DS `FallAssessmentPanel` /
// `PainAssessmentPanel`, 2.4.1) wrote the entry, because that is what Flutter reads:
//   Fall  — `isPresent`; per Morse category (`MORSE_QUESTIONS[i].id`) exactly one option row
//           `status: 'yes'`, the rest `'none'`, matched by option LABEL (the row's `title`).
//   Pain  — `isPresent`; `painScore` = the Wong-Baker band KEY (`'mild'`…); `onset` = ISO string
//           (date mode); `address`; `pharmacologicalInterventions`; chip fields under
//           `items[PAIN_FIELDS.id]` as the option LABEL (single) or label array (multi).
//
// Answers carry option CODES (the renderer's `qrOptionValue` = code ?? valueString ?? label); the
// wire carries LABELS. Every translation goes through the template's own `answerOption`, never a
// slug guess, so a re-coded option still maps by what the clinician saw.
//
// Fall maps BY POSITION: the DS's `fallAssessmentParts` question order ↔ `MORSE_QUESTIONS[i]`
// (`risk-qr-bridge.test.ts` pins the v4 seed's labels and scores against that list, so a
// reordered seed fails loudly instead of writing answers into the wrong category). Pain maps
// through an explicit linkId table — two fields share the label "Location", so a label match
// would be ambiguous there.
//
// Entry fields with no template seat (`id`, `title`, `notePlainText`, `isCompleted`, `createdOn`,
// `isEnterNumber`, a fall option's `t`) pass through untouched. Template fields with no wire seat
// (`risk-pain-scale`, `risk-fall-morse-total`) are never written to the entry.
import {
  fallAssessmentParts,
  painAssessmentParts,
  qrOptionValue,
  type QAnswerOption,
  type QAnswerValue,
  type QItem,
  type QRAnswerItem,
  type QuestionnaireResponse,
} from '@scripsteam/scrips-signal-ds';
import { MORSE_QUESTIONS } from './morse-questions';
import { morseCategory } from './risk-assessment-types';
import { PAIN_FIELDS, type PainFieldDef } from './pain-fields';
import type {
  FallAssessmentEntry,
  FallCategoryAnswer,
  PainAssessmentEntry,
  PainFieldValue,
} from './risk-assessment-types';

/** The template's detail linkIds (seed-owned strings) → the `PAIN_FIELDS` id each one writes. */
export const PAIN_LINK_TO_FIELD: Readonly<Record<string, string>> = {
  'risk-pain-onset': 'onset',
  'risk-pain-location': 'location-address',
  'risk-pain-character': 'pain-character',
  'risk-pain-timing': 'timing',
  'risk-pain-location-category': 'location-region',
  'risk-pain-relieving-factors': 'relieving-factors',
  'risk-pain-associated-signs': 'associated-signs-and-symptoms',
  'risk-pain-interventions': 'pharmacological-interventions',
  'risk-pain-psychological-status': 'psychological-status',
  'risk-pain-conscious': 'conscious',
};

/** The two bridged groups found in a template, and every linkId they own. */
export interface RiskBridgeScope {
  readonly fall?: QItem;
  readonly pain?: QItem;
  /** Every linkId inside (and including) the two groups — the answers that go to EncounterMetaData
   *  and must never reach the QuestionnaireResponse save. */
  readonly linkIds: ReadonlySet<string>;
}

function findByControl(items: readonly QItem[], control: string): QItem | undefined {
  for (const item of items) {
    if (item.type === 'group' && item.itemControl === control) return item;
    const hit = findByControl(item.item ?? [], control);
    if (hit) return hit;
  }
  return undefined;
}

function collectLinkIds(item: QItem, into: Set<string>): void {
  into.add(item.linkId);
  (item.item ?? []).forEach((child) => collectLinkIds(child, into));
}

/** Locate the `fall-assessment` / `wong-baker-pain` groups by STRUCTURE (itemControl), anywhere in
 *  the template — the same way the DS renderer decides to draw them. */
export function riskBridgeScope(items: readonly QItem[] | undefined, opts: { keepUnseatedInQr?: boolean } = {}): RiskBridgeScope {
  const fall = findByControl(items ?? [], 'fall-assessment');
  const pain = findByControl(items ?? [], 'wong-baker-pain');
  const linkIds = new Set<string>();
  if (fall) collectLinkIds(fall, linkIds);
  if (pain) collectLinkIds(pain, linkIds);
  // PROD-2947 · frame v2: the pain scale has no seat on the EncounterMetaData wire (the header above), so it stays out
  // of the bridged slice and is saved with the questionnaire answers instead — never stripped and lost.
  if (opts.keepUnseatedInQr && pain) {
    const scale = painAssessmentParts(pain).scale;
    if (scale) linkIds.delete(scale.linkId);
  }
  return { fall, pain, linkIds };
}

/** A label as the clinician reads it, folded so typographic and straight quotes compare equal
 *  (the v4 seed writes ‘Yes’ where the Flutter-mirrored list writes 'Yes'). */
export function normaliseLabel(label: string): string {
  return label
    .replace(/[‘’‛′]/g, "'")
    .replace(/[“”″]/g, '"')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function optionLabel(o: QAnswerOption): string {
  return o.valueCoding?.display ?? o.valueString ?? o.valueCoding?.code ?? '';
}

function optionByLabel(item: QItem, label: string): QAnswerOption | undefined {
  const want = normaliseLabel(label);
  return (item.answerOption ?? []).find((o) => normaliseLabel(optionLabel(o)) === want);
}

function optionByValue(item: QItem, value: string): QAnswerOption | undefined {
  return (item.answerOption ?? []).find((o) => qrOptionValue(o) === value);
}

function answersMap(answers: readonly QRAnswerItem[]): Map<string, QAnswerValue> {
  const m = new Map<string, QAnswerValue>();
  answers.forEach((a) => m.set(a.linkId, a.value));
  return m;
}

const isAnswered = (v: QAnswerValue): boolean =>
  v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);

/** An answer's selected option codes, whether the item is single (`'yes'`) or repeats (`['a']`). */
function answerCodes(v: QAnswerValue): string[] {
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string' && x !== '');
  return typeof v === 'string' && v !== '' ? [v] : [];
}

// ── HYDRATE: entries → answers ────────────────────────────────────────────────────────────────

function fallAnswers(fall: QItem, head: FallAssessmentEntry): QRAnswerItem[] {
  const { present, questions } = fallAssessmentParts(fall);
  const out: QRAnswerItem[] = [];
  // Emitted explicitly — true AND false — so the switch reflects the stored entry and never falls
  // back to the authored `initial`.
  if (present) out.push({ linkId: present.linkId, value: head.isPresent });
  questions.forEach((q, i) => {
    const def = MORSE_QUESTIONS[i];
    if (!def) return;
    const category = head.items.find((c) => c.id === def.id);
    const chosen = category?.items.find((o) => o.status === 'yes');
    if (!chosen) return;
    const candidates = (q.answerOption ?? []).filter(
      (o) => normaliseLabel(optionLabel(o)) === normaliseLabel(chosen.title),
    );
    // Label is the identity; the score is the cross-check that picks between equal labels.
    const opt = candidates.find((o) => (o.ordinalValue ?? 0) === chosen.score) ?? candidates[0];
    if (opt) out.push({ linkId: q.linkId, value: qrOptionValue(opt) });
  });
  return out;
}

function painFieldDef(fieldId: string): PainFieldDef | undefined {
  return PAIN_FIELDS.find((f) => f.id === fieldId);
}

function painDetailAnswer(item: QItem, def: PainFieldDef, head: PainAssessmentEntry): QAnswerValue {
  switch (def.storesTo) {
    case 'onset': {
      // A numeric onset (`isEnterNumber`) has no dateTime form — left unanswered, and the persist
      // side preserves it.
      if (head.isEnterNumber || !head.onset) return undefined;
      const d = new Date(head.onset);
      return Number.isNaN(d.getTime()) ? undefined : d;
    }
    case 'address':
      return head.address ? head.address : undefined;
    case 'pharmacologicalInterventions':
      return head.pharmacologicalInterventions ? head.pharmacologicalInterventions : undefined;
    default: {
      const stored: PainFieldValue | undefined = head.items[def.id];
      const labels = Array.isArray(stored) ? stored : typeof stored === 'string' && stored !== '' ? [stored] : [];
      const codes = labels
        .map((label) => optionByLabel(item, label))
        .filter((o): o is QAnswerOption => o !== undefined)
        .map(qrOptionValue);
      if (codes.length === 0) return undefined;
      // A single-select template item holds one code; the wire's multi array collapses to its
      // first selection there (lossy — the v4 seed authors the three chip-multi fields without
      // `repeats`).
      return item.repeats ? codes : codes[0];
    }
  }
}

function painAnswers(pain: QItem, head: PainAssessmentEntry): QRAnswerItem[] {
  const { present, wongBaker, details } = painAssessmentParts(pain);
  const out: QRAnswerItem[] = [];
  if (present) out.push({ linkId: present.linkId, value: head.isPresent });
  if (wongBaker && head.painScore && optionByValue(wongBaker, head.painScore)) {
    out.push({ linkId: wongBaker.linkId, value: head.painScore });
  }
  (details?.item ?? []).forEach((item) => {
    const fieldId = PAIN_LINK_TO_FIELD[item.linkId];
    const def = fieldId ? painFieldDef(fieldId) : undefined;
    if (!def) return;
    const value = painDetailAnswer(item, def, head);
    if (isAnswered(value)) out.push({ linkId: item.linkId, value });
  });
  return out;
}

/**
 * HYDRATE — the renderer answers for the bridged Risk groups, from the stored entry heads.
 * A missing head (no stored entry) contributes nothing, so the template's authored `initial`
 * governs a fresh encounter.
 */
export interface RiskHydrateOptions {
  /** Frame v2 (Scrips Agent review at 665cde84): "present" on a record that is NOT completed is a default (Flutter's
   *  "start" writes `isPresent: true`, a fresh record `false`), not the clinician's answer — so it is not read back
   *  into the note (the line reads "Not asked · select"; absence is not "No", K3) and cannot complete the record. */
  presentOnlyWhenCompleted?: boolean;
}

export function riskAnswersFromEntries(
  items: readonly QItem[] | undefined,
  fallHead: FallAssessmentEntry | undefined,
  painHead: PainAssessmentEntry | undefined,
  opts: RiskHydrateOptions = {},
): QRAnswerItem[] {
  const { fall, pain } = riskBridgeScope(items);
  const keepPresent = (head: { isCompleted: boolean }) => !opts.presentOnlyWhenCompleted || head.isCompleted;
  const withoutPresent = (answers: QRAnswerItem[], present: QItem | undefined) =>
    present ? answers.filter((a) => a.linkId !== present.linkId) : answers;
  return [
    ...(fall && fallHead
      ? keepPresent(fallHead)
        ? fallAnswers(fall, fallHead)
        : withoutPresent(fallAnswers(fall, fallHead), fallAssessmentParts(fall).present)
      : []),
    ...(pain && painHead
      ? keepPresent(painHead)
        ? painAnswers(pain, painHead)
        : withoutPresent(painAnswers(pain, painHead), painAssessmentParts(pain).present)
      : []),
  ];
}

// ── PERSIST: answers → entries ────────────────────────────────────────────────────────────────

/** A fresh category for a Morse question the stored entry lacks (the OFF path only; see `morseCategory`). */
const freshCategory = (index: number): FallCategoryAnswer | undefined => morseCategory(index);

/**
 * Founder ruling, 7 Oct 2026 (licensing): in frame v2 the Morse rows and the Wong-Baker band are READ-ONLY HISTORY —
 * read from an old record for display, never written: no new Morse row is minted and a stored one is never changed;
 * `painScore` passes through as stored. `isPresent` and the pain details are not licensed content and save as before.
 */
export interface RiskWriteOptions {
  licensedReadOnly?: boolean;
}

function nextFall(fall: QItem, values: Map<string, QAnswerValue>, head: FallAssessmentEntry, opts: RiskWriteOptions = {}): FallAssessmentEntry {
  const { present, questions } = fallAssessmentParts(fall);
  if (opts.licensedReadOnly) {
    const presentValue = present ? values.get(present.linkId) : undefined;
    return { ...head, isPresent: typeof presentValue === 'boolean' ? presentValue : head.isPresent };
  }
  let items = [...head.items];
  questions.forEach((q, i) => {
    const def = MORSE_QUESTIONS[i];
    if (!def) return;
    const at = items.findIndex((c) => c.id === def.id);
    const category = at >= 0 ? items[at] : freshCategory(i);
    if (!category) return;
    const code = answerCodes(values.get(q.linkId))[0];
    const opt = code !== undefined ? optionByValue(q, code) : undefined;
    const chosen = opt ? normaliseLabel(optionLabel(opt)) : undefined;
    const updated: FallCategoryAnswer = {
      ...category,
      items: category.items.map((row) => ({
        ...row,
        status: chosen !== undefined && normaliseLabel(row.title) === chosen ? ('yes' as const) : ('none' as const),
      })),
    };
    items = at >= 0 ? items.map((c, j) => (j === at ? updated : c)) : [...items, updated];
  });
  const presentValue = present ? values.get(present.linkId) : undefined;
  return {
    ...head,
    items,
    isPresent: typeof presentValue === 'boolean' ? presentValue : head.isPresent,
  };
}

function nextPain(pain: QItem, values: Map<string, QAnswerValue>, head: PainAssessmentEntry, opts: RiskWriteOptions = {}): PainAssessmentEntry {
  const { present, wongBaker, details } = painAssessmentParts(pain);
  const next: { -readonly [K in keyof PainAssessmentEntry]: PainAssessmentEntry[K] } = { ...head };
  const items: Record<string, PainFieldValue> = { ...head.items };

  const presentValue = present ? values.get(present.linkId) : undefined;
  if (typeof presentValue === 'boolean') next.isPresent = presentValue;

  if (wongBaker && !opts.licensedReadOnly) {
    const code = answerCodes(values.get(wongBaker.linkId))[0];
    next.painScore = code !== undefined && optionByValue(wongBaker, code) ? code : undefined;
  }

  (details?.item ?? []).forEach((item) => {
    const fieldId = PAIN_LINK_TO_FIELD[item.linkId];
    const def = fieldId ? painFieldDef(fieldId) : undefined;
    if (!def) return;
    const v = values.get(item.linkId);
    switch (def.storesTo) {
      case 'onset':
        if (v instanceof Date) {
          next.onset = v.toISOString();
          next.isEnterNumber = false;
        } else if (typeof v === 'string' && v !== '') {
          next.onset = v;
          next.isEnterNumber = false;
        } else if (!head.isEnterNumber) {
          next.onset = '';
        }
        return;
      case 'address':
        next.address = typeof v === 'string' ? v : '';
        return;
      case 'pharmacologicalInterventions':
        next.pharmacologicalInterventions = typeof v === 'string' ? v : '';
        return;
      default: {
        const labels = answerCodes(v)
          .map((code) => optionByValue(item, code))
          .filter((o): o is QAnswerOption => o !== undefined)
          .map(optionLabel);
        items[def.id] = def.type === 'chip-multi' ? labels : (labels[0] ?? '');
      }
    }
  });
  next.items = items;
  return next;
}

/**
 * PERSIST — the updated entry heads for a set of bridged Risk answers. Every template-seated field
 * is rewritten from the answers (an unanswered field clears, which is what Clear All means);
 * everything without a template seat passes through from the head.
 */
export function entriesFromRiskAnswers(
  items: readonly QItem[] | undefined,
  riskAnswers: readonly QRAnswerItem[],
  fallHead: FallAssessmentEntry,
  painHead: PainAssessmentEntry,
  opts: RiskWriteOptions = {},
): { fall: FallAssessmentEntry; pain: PainAssessmentEntry } {
  const { fall, pain } = riskBridgeScope(items);
  const values = answersMap(riskAnswers);
  return {
    fall: fall ? nextFall(fall, values, fallHead, opts) : fallHead,
    pain: pain ? nextPain(pain, values, painHead, opts) : painHead,
  };
}

/** The managed lines Flutter writes into the Fall / Pain note (`projectFreeScreens`'s exact formats), read back for
 *  display: the CDC STEADI line and the NRS line, each split into its label and its value. */
export interface ManagedRiskLine {
  label: string;
  value: string;
  /** STEADI: YES to a key question. NRS: the score. */
  atRisk?: boolean;
  score?: number;
}
export type RiskManaged = { steadi?: ManagedRiskLine; nrs?: ManagedRiskLine };
export function managedRiskLines(fallHead: FallAssessmentEntry | undefined, painHead: PainAssessmentEntry | undefined): RiskManaged {
  const find = (note: string | undefined, re: RegExp) => (note ?? '').split('\n').map((l) => l.trim()).find((l) => re.test(l));
  const out: { steadi?: ManagedRiskLine; nrs?: ManagedRiskLine } = {};
  const steadi = find(fallHead?.notePlainText, STEADI_NOTE_LINE);
  if (steadi) {
    const value = steadi.slice(STEADI_NOTE_PREFIX.length).trim();
    // The line carries its own verdict: " — at risk (YES to a key question)" whenever a key question is YES.
    out.steadi = { label: STEADI_NOTE_PREFIX.replace(/:$/, ''), value, atRisk: steadi.endsWith(STEADI_AT_RISK) };
  }
  const nrs = find(painHead?.notePlainText, NRS_NOTE_LINE);
  if (nrs) {
    const value = nrs.slice(NRS_NOTE_PREFIX.length).trim();
    out.nrs = { label: NRS_NOTE_PREFIX.replace(/:$/, ''), value, score: Number(value) };
  }
  return out;
}

// ── FREE-SCREEN PROJECTION: NRS + STEADI → the Flutter-read entries (PROD-2791) ───────────────
//
// Templates authored by `scripts/lib/template-pack.mjs` (rule P13) replace the licensed cards with the
// 0–10 Numeric Rating Scale (`risk-nrs-pain`) and the CDC STEADI three key questions (`risk-steadi`).
// Unlike the cards, those answers are STORED in the QuestionnaireResponse like any other item — they
// are not bridged out of it. Flutter reads only EncounterMetaData Fall/PainAssessment, so this PROJECTS
// them one way onto the entry heads, into fields that can carry them honestly and nowhere else:
//   Pain · `isPresent` ("Pain present") = score > 0. `notePlainText` gets one managed line with the score.
//          `painScore` is the WONG-BAKER band key (`'mild'`…) → never written from an NRS score.
//   Fall · `isPresent` ("Fall risk present") = YES to any key question (CDC: at risk if YES to any); all
//          three NO → false; incomplete → unchanged. `notePlainText` gets one managed line with the answers,
//          the fall count and the injury. `items` are the MORSE categories (history of falling "within
//          3-6 months", with Morse points) → never written from STEADI (past year, no points).
// Display parity only: neither entry reaches the HIE — Scrips.Integration.Nabidh (origin/master 26be4f7)
// and Scrips.Integration.Malaffi (origin/master bc63a4b) reference no Fall/Pain assessment, EncounterMetaData
// or pain field at all (searched 2026-10-05).

/** The leaf linkIds `template-pack.mjs` authors (`nrsSection` / `steadiSection`); a test pins the coupling. */
export const NRS_SCORE_LINK = 'risk-nrs-pain-score';
export const STEADI_LINKS = {
  unsteady: 'risk-steadi-unsteady',
  worries: 'risk-steadi-worries',
  fallen: 'risk-steadi-fallen',
  count: 'risk-steadi-fall-count',
  injured: 'risk-steadi-injured',
} as const;
const NRS_NOTE_PREFIX = 'Pain NRS 0-10 (self-report):';
const STEADI_NOTE_PREFIX = 'CDC STEADI key questions:';
const STEADI_AT_RISK = ' \u2014 at risk (YES to a key question)';
/* The managed lines are matched by their EXACT generated format, not by prefix: a clinician line that
   merely starts with the same words ("Pain NRS 0-10 (self-report): only when coughing") is their note and
   is kept. Free text placed in a line goes through `noteSafe`, so the format stays matchable. */
const NRS_NOTE_LINE = /^Pain NRS 0-10 \(self-report\): (?:10|[0-9])$/;
const STEADI_PART = '(?:unsteady when standing or walking: (?:Yes|No)|worries about falling: (?:Yes|No)|fallen in past year: (?:Yes|No)|times fallen: [^;\\n\u2014]+|injured: [^;\\n\u2014]+)';
const STEADI_NOTE_LINE = new RegExp(
  `^CDC STEADI key questions: ${STEADI_PART}(?:; ${STEADI_PART})*(?: \u2014 at risk \\(YES to a key question\\)| \u2014 not at risk on the key questions)?$`,
);
/** The managed STEADI line's parts, by the words `projectFreeScreens` writes before each value. */
const STEADI_PART_KEYS: Record<string, keyof typeof STEADI_LINKS> = {
  'unsteady when standing or walking': 'unsteady',
  'worries about falling': 'worries',
  'fallen in past year': 'fallen',
  'times fallen': 'count',
  injured: 'injured',
};
const STEADI_VERDICT = / \u2014 (?:at risk \(YES to a key question\)|not at risk on the key questions)$/;

/**
 * PROD-3128 · the managed STEADI line of a Fall note, read back into its parts as written ("Yes" / "No" for a key
 * question or the injury; the fall count's text). Undefined when the note holds no line in the exact managed format.
 */
export function steadiNoteParts(note: string | undefined): Partial<Record<keyof typeof STEADI_LINKS, string>> | undefined {
  const line = (note ?? '').split('\n').map((l) => l.trim()).find((l) => STEADI_NOTE_LINE.test(l));
  if (!line) return undefined;
  const out: Partial<Record<keyof typeof STEADI_LINKS, string>> = {};
  for (const part of line.slice(STEADI_NOTE_PREFIX.length).trim().replace(STEADI_VERDICT, '').split('; ')) {
    const at = part.indexOf(': ');
    const key = at > 0 ? STEADI_PART_KEYS[part.slice(0, at)] : undefined;
    if (key) out[key] = part.slice(at + 2);
  }
  return out;
}

/** Free text made safe for a managed line: no line break, `;` or em dash (the format's separators). */
const noteSafe = (s: string): string => s.replace(/[\n\r;\u2014]+/g, ' ').replace(/\s+/g, ' ').trim();

function findItem(items: readonly QItem[], linkId: string): QItem | undefined {
  for (const item of items) {
    if (item.linkId === linkId) return item;
    const hit = findItem(item.item ?? [], linkId);
    if (hit) return hit;
  }
  return undefined;
}

/** The free-screen items a template authors, and their linkIds (which stay in the QR). */
export interface FreeScreenScope {
  readonly nrs?: QItem;
  readonly steadi: Partial<Record<keyof typeof STEADI_LINKS, QItem>>;
  readonly linkIds: ReadonlySet<string>;
}

export function freeScreenScope(items: readonly QItem[] | undefined): FreeScreenScope {
  const all = items ?? [];
  const nrs = findItem(all, NRS_SCORE_LINK);
  const steadi: Partial<Record<keyof typeof STEADI_LINKS, QItem>> = {};
  (Object.keys(STEADI_LINKS) as (keyof typeof STEADI_LINKS)[]).forEach((k) => {
    const hit = findItem(all, STEADI_LINKS[k]);
    if (hit) steadi[k] = hit;
  });
  const linkIds = new Set<string>([
    ...(nrs ? [nrs.linkId] : []),
    ...Object.values(steadi).filter((i): i is QItem => i !== undefined).map((i) => i.linkId),
  ]);
  return { nrs, steadi, linkIds };
}

/** The response's free-screen answers. */
export function freeScreenAnswersOf(response: QuestionnaireResponse, scope: FreeScreenScope): QRAnswerItem[] {
  return response.answers.filter((a) => scope.linkIds.has(a.linkId));
}

/** What "Discard This Note" keeps: every Risk-step answer — the bridged cards' AND the free screens'.
 *  The free answers are projected onto the EncounterMetaData heads, which a discard never writes, so
 *  clearing them from the QR alone would leave "present" and the note line standing over a blank step. */
export function riskStepAnswersOf(response: QuestionnaireResponse, scope: RiskBridgeScope, free: FreeScreenScope): QRAnswerItem[] {
  return response.answers.filter((a) => scope.linkIds.has(a.linkId) || free.linkIds.has(a.linkId));
}

/** Replace (or remove, when `line` is undefined) the one note line in the managed format; every other
 *  line — the clinician's own note, typed in Flutter — is kept as it was. */
function withManagedLine(note: string, managed: RegExp, line: string | undefined): string {
  const kept = note === '' ? [] : note.split('\n').filter((l) => !managed.test(l));
  return [...kept, ...(line ? [line] : [])].join('\n');
}

function selectedLabel(item: QItem | undefined, values: Map<string, QAnswerValue>): string | undefined {
  if (!item) return undefined;
  const code = answerCodes(values.get(item.linkId))[0];
  const opt = code !== undefined ? optionByValue(item, code) : undefined;
  return opt ? optionLabel(opt) : undefined;
}

function nrsScore(item: QItem | undefined, values: Map<string, QAnswerValue>): number | undefined {
  if (!item) return undefined;
  const code = answerCodes(values.get(item.linkId))[0];
  const opt = code !== undefined ? optionByValue(item, code) : undefined;
  const n = opt?.ordinalValue ?? (opt ? Number(optionLabel(opt)) : undefined);
  return n !== undefined && Number.isInteger(n) && n >= 0 && n <= 10 ? n : undefined;
}

/**
 * The free screens' results on the note's answers (PROD-2947, 7 Oct 2026 ruling), for the nurse's problems: STEADI
 * `atRisk` true when any key question is YES, false when all three are NO, absent otherwise; the NRS score when
 * answered. Same derivation as `projectFreeScreens`.
 */
export function freeScreenResults(
  items: readonly QItem[] | undefined,
  values: Readonly<Record<string, QAnswerValue>>,
): { atRisk?: boolean; nrs?: number } {
  const scope = freeScreenScope(items);
  const map = new Map(Object.entries(values));
  const out: { atRisk?: boolean; nrs?: number } = {};
  const yes = (k: 'unsteady' | 'worries' | 'fallen') => {
    const label = selectedLabel(scope.steadi[k], map);
    return label === undefined ? undefined : normaliseLabel(label) === 'yes';
  };
  const key = [yes('unsteady'), yes('worries'), yes('fallen')];
  if (key.some((v) => v === true)) out.atRisk = true;
  else if (key.every((v) => v === false)) out.atRisk = false;
  const n = nrsScore(scope.nrs, map);
  if (n !== undefined) out.nrs = n;
  return out;
}

// ── COMPLETION: Flutter's "Done", for the frame v2 autosave (Andrew's review item 6, 7 Oct) ──────────────────────────
//
// Flutter READS `isCompleted` and `createdOn` on the Fall / Pain record (scrips_msp1_flutter_shared, origin/dev):
//   · risk_assessment_screen.dart:151 — when the first record is NOT completed, "start" edits it instead of adding a
//     new assessment;
//   · fall_assessment_widget.dart:73 / pain_assesment_widget.dart:89 — the compact view lists records (with their
//     `createdOn`) only once the first is completed;
//   · fall_assessment_widget.dart:395 / pain_assesment_widget.dart:265 — Flutter's own save, the "Done" button, sets
//     `isCompleted: true, createdOn: now`. Done is the ONLY completion path; Flutter never sets it back to false.
// Frame v2 has no Done (the contract: no "Done"), so its autosave marks the record complete when the assessment's
// result is recorded — what pressing Done would have saved:
//   · Fall: on a template with CDC STEADI, the STEADI result (a YES to any key question, or NO to all three); else
//     "Fall risk present" answered Yes or No (the only Fall answer a new note asks since the 7 Oct 2026 ruling).
//   · Pain: on a template with the NRS, the NRS score; else "Pain present" answered Yes or No.
// While partial, `isCompleted` stays false. `createdOn` is set once, at the first completion, and never moved by a
// later edit; a completed record is never set back to not completed (Flutter has no such path).

/** Whether each assessment's result is recorded in these answers (the bridged and the free-screen answers). */
export function riskCompletion(
  items: readonly QItem[] | undefined,
  answers: readonly QRAnswerItem[],
  /** Scrips Agent review at 665cde84: the linkIds the clinician answered (changed) in this session. When given, a
   *  "present" value counts only if it is one of them — a stored default (`isPresent: false` on a fresh record) is not
   *  a result. */
  answered?: ReadonlySet<string>,
): { fall: boolean; pain: boolean } {
  const values = answersMap(answers);
  const bridged = riskBridgeScope(items);
  const free = freeScreenScope(items);
  const presentAnswered = (group: QItem | undefined, kind: 'fall' | 'pain') => {
    if (!group) return false;
    const present = kind === 'fall' ? fallAssessmentParts(group).present : painAssessmentParts(group).present;
    if (present === undefined || typeof values.get(present.linkId) !== 'boolean') return false;
    return !answered || answered.has(present.linkId);
  };
  const results = freeScreenResults(items, Object.fromEntries(values));
  const hasSteadi = (['unsteady', 'worries', 'fallen'] as const).some((k) => free.steadi[k] !== undefined);
  return {
    fall: hasSteadi ? results.atRisk !== undefined : presentAnswered(bridged.fall, 'fall'),
    pain: free.nrs ? results.nrs !== undefined : presentAnswered(bridged.pain, 'pain'),
  };
}

/** The "present" linkId of the Fall or Pain group: the only result `riskCompletion` gates on `answered`. Scrips Agent
 *  review at bea4b95b: an edit that starts a NEW record from a completed one carries the old result into it, so the
 *  hook counts this linkId as answered for the new record (Flutter's Start then Done would save it completed). */
export function riskPresentLinkId(items: readonly QItem[] | undefined, kind: 'fall' | 'pain'): string | undefined {
  const { fall, pain } = riskBridgeScope(items);
  if (kind === 'fall') return fall ? fallAssessmentParts(fall).present?.linkId : undefined;
  return pain ? painAssessmentParts(pain).present?.linkId : undefined;
}

/** The entry with Flutter's "Done" applied at the first completion: `isCompleted: true` and `createdOn` = `now` (the
 *  caller passes Flutter's record-time format on the clinic's clock, `clinicWallClockStamp`). A partial assessment,
 *  or one already completed, is returned unchanged. */
export function withCompletion<T extends { readonly isCompleted: boolean; readonly createdOn: string | undefined }>(entry: T, complete: boolean, now: string): T {
  if (!complete || entry.isCompleted) return entry;
  return { ...entry, isCompleted: true, createdOn: now };
}

/**
 * PROJECT — the entry heads with the free-screen answers copied in (see the block comment above for what
 * may and may not be written). Fields the screens cannot carry pass through from the heads untouched.
 */
export function projectFreeScreens(
  items: readonly QItem[] | undefined,
  answers: readonly QRAnswerItem[],
  fallHead: FallAssessmentEntry,
  painHead: PainAssessmentEntry,
  /** PROD-3128: false when STEADI is the follow-up under the template's own "Fall risk present" (`steadi-follow-up.ts`).
   *  The clinician's answer to that question is then `isPresent`; STEADI writes only its managed line. Default true. */
  { steadiSetsPresent = true }: { steadiSetsPresent?: boolean } = {},
): { fall: FallAssessmentEntry; pain: PainAssessmentEntry } {
  const scope = freeScreenScope(items);
  const values = answersMap(answers);

  let pain = painHead;
  if (scope.nrs) {
    const score = nrsScore(scope.nrs, values);
    pain = {
      ...painHead,
      isPresent: score === undefined ? painHead.isPresent : score > 0,
      notePlainText: withManagedLine(painHead.notePlainText, NRS_NOTE_LINE, score === undefined ? undefined : `${NRS_NOTE_PREFIX} ${score}`),
    };
  }

  let fall = fallHead;
  if (Object.keys(scope.steadi).length > 0) {
    const yn = (k: 'unsteady' | 'worries' | 'fallen') => {
      const label = selectedLabel(scope.steadi[k], values);
      return label === undefined ? undefined : normaliseLabel(label) === 'yes';
    };
    const key = { unsteady: yn('unsteady'), worries: yn('worries'), fallen: yn('fallen') };
    const answered = Object.values(key).filter((v) => v !== undefined);
    const anyYes = answered.some((v) => v === true);
    const allNo = answered.length === 3 && answered.every((v) => v === false);
    const countRaw = scope.steadi.count ? values.get(scope.steadi.count.linkId) : undefined;
    const countText = typeof countRaw === 'number' || typeof countRaw === 'string' ? noteSafe(String(countRaw)) : '';
    const count = countText !== '' ? countText : undefined;
    const injuredText = noteSafe(selectedLabel(scope.steadi.injured, values) ?? '');
    const injured = injuredText !== '' ? injuredText : undefined;
    const yesNo = (v: boolean | undefined) => (v === undefined ? undefined : v ? 'Yes' : 'No');
    const parts = [
      key.unsteady !== undefined ? `unsteady when standing or walking: ${yesNo(key.unsteady)}` : undefined,
      key.worries !== undefined ? `worries about falling: ${yesNo(key.worries)}` : undefined,
      key.fallen !== undefined ? `fallen in past year: ${yesNo(key.fallen)}` : undefined,
      count !== undefined ? `times fallen: ${count}` : undefined,
      injured !== undefined ? `injured: ${injured}` : undefined,
    ].filter((p): p is string => p !== undefined);
    const verdict = anyYes ? STEADI_AT_RISK : allNo ? ' — not at risk on the key questions' : '';
    fall = {
      ...fallHead,
      isPresent: !steadiSetsPresent ? fallHead.isPresent : anyYes ? true : allNo ? false : fallHead.isPresent,
      notePlainText: withManagedLine(fallHead.notePlainText, STEADI_NOTE_LINE, parts.length ? `${STEADI_NOTE_PREFIX} ${parts.join('; ')}${verdict}` : undefined),
    };
  }
  return { fall, pain };
}

// ── The QuestionnaireResponse seam ────────────────────────────────────────────────────────────

/** The response with every bridged Risk answer removed — what the QR save may carry. `alsoExcluded`: answers kept only
 *  in the Fall record (PROD-3128, the STEADI follow-up). */
export function withoutRiskAnswers(response: QuestionnaireResponse, scope: RiskBridgeScope, alsoExcluded?: ReadonlySet<string>): QuestionnaireResponse {
  if (scope.linkIds.size === 0 && !alsoExcluded?.size) return response;
  const answers = response.answers.filter((a) => !scope.linkIds.has(a.linkId) && !alsoExcluded?.has(a.linkId));
  return answers.length === response.answers.length ? response : { ...response, answers };
}

/** The response with its bridged Risk answers replaced by `riskAnswers`. */
export function withRiskAnswers(
  response: QuestionnaireResponse,
  scope: RiskBridgeScope,
  riskAnswers: readonly QRAnswerItem[],
): QuestionnaireResponse {
  return { ...response, answers: [...withoutRiskAnswers(response, scope).answers, ...riskAnswers] };
}

/** The bridged Risk answers a response currently holds. */
export function riskAnswersOf(response: QuestionnaireResponse, scope: RiskBridgeScope): QRAnswerItem[] {
  return response.answers.filter((a) => scope.linkIds.has(a.linkId));
}
